import * as THREE from "three";

/**
 * Create an infinity/figure-8 shaped gradient texture for blob shadow (two feet)
 * @param {number} width - Texture width in pixels
 * @param {number} height - Texture height in pixels
 * @returns {THREE.CanvasTexture}
 */
function createBlobShadowTexture(size = 256) {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");

  // Simple radial gradient for natural contact shadow
  const centerX = size / 2;
  const centerY = size / 2;
  const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, size / 2);
  gradient.addColorStop(0, "rgba(0, 0, 0, 0.35)");
  gradient.addColorStop(0.4, "rgba(0, 0, 0, 0.20)");
  gradient.addColorStop(0.7, "rgba(0, 0, 0, 0.075)");
  gradient.addColorStop(1, "rgba(0, 0, 0, 0)");

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}

/**
 * Create blob shadow mesh (rectangular gradient under feet)
 * @returns {THREE.Mesh}
 */
export function createBlobShadow() {
  const texture = createBlobShadowTexture(256);
  // Oval shadow under avatar (width x depth)
  const geometry = new THREE.PlaneGeometry(0.9, 0.6);
  const material = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    depthWrite: false,
    opacity: 1
  });

  const shadow = new THREE.Mesh(geometry, material);
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.y = 0.001; // Slightly above ground to prevent z-fighting
  shadow.name = "BlobShadow";

  return shadow;
}

/**
 * Create studio lighting setup with optional real-time shadows
 * Returns a Group containing all lights for easy disposal
 * @param {boolean} enableRealTimeShadows - Enable real-time shadow mapping on key light
 * @returns {THREE.Group} Group containing all studio lights
 */
export function createStudioLighting(enableRealTimeShadows = false) {
  const lightingGroup = new THREE.Group();
  lightingGroup.name = "StudioLighting";

  // Key light - nearly vertical for tight shadow under feet
  const keyLight = new THREE.DirectionalLight(0xffffff, 2.5);
  keyLight.name = "KeyLight";
  keyLight.position.set(0.5, 8, 0.5); // Almost directly above, slight offset

  if (enableRealTimeShadows) {
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.width = 512;  // Low res for performance
    keyLight.shadow.mapSize.height = 512;
    keyLight.shadow.camera.near = 0.1;
    keyLight.shadow.camera.far = 12;
    // Tight frustum for avatar-only shadows
    keyLight.shadow.camera.left = -1;
    keyLight.shadow.camera.right = 1;
    keyLight.shadow.camera.top = 1;
    keyLight.shadow.camera.bottom = -1;
    keyLight.shadow.bias = -0.001;
    keyLight.shadow.radius = 2; // Soft edge
    keyLight.shadow.autoUpdate = false; // Manual updates only
  }

  lightingGroup.add(keyLight);

  // Fill light - softer light from opposite side (no shadows)
  const fillLight = new THREE.DirectionalLight(0xffffff, 0.8);
  fillLight.name = "FillLight";
  fillLight.position.set(-2, 2, -1);
  lightingGroup.add(fillLight);

  // Rim/back light - subtle highlight (no shadows)
  const rimLight = new THREE.DirectionalLight(0xffffff, 0.5);
  rimLight.name = "RimLight";
  rimLight.position.set(0, 2, -3);
  lightingGroup.add(rimLight);

  return lightingGroup;
}

/**
 * Create blob shadow (baked elliptical gradient under feet)
 * @returns {THREE.Mesh} Blob shadow mesh
 */
export function createShadowGround() {
  return createBlobShadow();
}

/**
 * Enable shadow casting on avatar meshes
 * @param {THREE.Object3D} avatarNode - The avatar root node
 */
export function enableAvatarShadows(avatarNode) {
  if (!avatarNode) return;
  avatarNode.traverse((child) => {
    if (child.isMesh) {
      child.castShadow = true;
    }
  });
}

/**
 * Gets the key light from studio lighting group (for shadow updates)
 * @param {THREE.Group} studioLightingGroup - The studio lighting group
 * @returns {THREE.DirectionalLight|undefined}
 */
export function getKeyLight(studioLightingGroup) {
  return studioLightingGroup?.getObjectByName("KeyLight");
}

/**
 * Creates a throttled shadow update function
 * @param {THREE.DirectionalLight} light - Light with shadow
 * @param {number} intervalMs - Minimum ms between updates (default 100ms = 10fps)
 * @returns {function} Function to request shadow update
 */
export function createThrottledShadowUpdater(light, intervalMs = 100) {
  let lastUpdate = 0;
  let pending = false;

  return function requestShadowUpdate() {
    if (!light?.shadow) return;

    const now = performance.now();
    if (now - lastUpdate >= intervalMs) {
      light.shadow.needsUpdate = true;
      lastUpdate = now;
      pending = false;
    } else if (!pending) {
      pending = true;
      setTimeout(() => {
        light.shadow.needsUpdate = true;
        lastUpdate = performance.now();
        pending = false;
      }, intervalMs - (now - lastUpdate));
    }
  };
}

/**
 * Create shadow receiving ground plane (for real-time shadows)
 * @returns {THREE.Mesh} Shadow receiver mesh
 */
export function createShadowReceiver() {
  const geometry = new THREE.PlaneGeometry(3, 3);
  const material = new THREE.ShadowMaterial({
    opacity: 0.3, // Subtle shadow
  });
  const plane = new THREE.Mesh(geometry, material);
  plane.name = "ShadowReceiver";
  plane.rotation.x = -Math.PI / 2;
  plane.receiveShadow = true;
  plane.position.y = 0.001; // Prevent z-fighting
  return plane;
}

/**
 * Dispose lighting resources
 * @param {THREE.Group} lightingGroup - The studio lighting group
 */
export function disposeStudioLighting(lightingGroup) {
  if (lightingGroup) {
    lightingGroup.traverse(obj => {
      if (obj.isLight && obj.shadow?.map) {
        obj.shadow.map.dispose();
      }
    });
  }
}
